import type { MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import { forwardRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import {
  createReadableClass,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import type { ShowMoreToggleLinesFn } from '@re-dev/react-truncate';

export type ShortenerClickHandlerFn = (
  e: MouseEvent<HTMLSpanElement>
) => boolean | void;

interface ControlProps extends PropsWithChildren {
  controlSize: number;
  controlColor: string;
  controlTopMargin: number;
  withControlUnderline: boolean;
  isShortened: boolean;
  toggleLines?: ShowMoreToggleLinesFn;
  onShortenerClick?: ShortenerClickHandlerFn;
}

export const Control = forwardRef<HTMLDivElement, ControlProps>(
  (
    {
      children,
      isShortened,
      controlSize,
      controlColor,
      controlTopMargin,
      withControlUnderline,
      toggleLines,
      onShortenerClick
    },
    forwardingRef
  ) => {
    const clickHandle: MouseEventHandler<HTMLSpanElement> = useCallback(
      (e) => {
        e.stopPropagation();

        const shouldDisableToggleLines = onShortenerClick?.(e);

        if (!shouldDisableToggleLines) {
          toggleLines?.(e);
        }
      },
      [onShortenerClick, toggleLines]
    );

    return (
      <BaseControl
        ref={forwardingRef}
        className={createReadableClass({
          component: 'text-shortener',
          element: 'control',
          index: isShortened ? 'open' : 'hide'
        })}
        onClick={clickHandle}
        onKeyDown={getHandleActionKeyDown(clickHandle)}
        _fontSize={controlSize}
        _color={controlColor}
        _withUnderline={withControlUnderline}
        _topMargin={controlTopMargin}
      >
        {children}
      </BaseControl>
    );
  }
);

const BaseControl = styled.div.attrs<{
  _fontSize: number;
  _color: string;
  _withUnderline: boolean;
  _topMargin: number;
}>({
  role: 'button',
  tabIndex: 0
})`
  display: inline-block;
  cursor: pointer;
  margin-top: ${({ _topMargin }) => _topMargin}px;
  font-size: ${({ _fontSize }) => _fontSize}px;
  color: ${({ _color }) => _color};

  ${({ _withUnderline }) =>
    _withUnderline &&
    css`
      &:hover,
      &:focus {
        text-decoration: underline;
      }
    `}
`;
